import React, { useEffect, useState } from 'react';
import { Form, Checkbox, Radio } from 'antd';
import { FormInput, IconDoer, IconSmallArrow, OrangeButton, TooltipIcon } from 'components';
import classNames from 'classnames';
import { getRequestData, updatePartner } from 'api/api';
import { useLocation, useNavigate } from 'react-router-dom';
import { endpoints } from 'api/endpoints';
import { allowOnlyDigits, getIdFromPath } from 'utils/helpers';
import { ROLE_TYPES, SLH_TYPES } from 'api/constants';
import { useUser } from 'context/userContext';

const defaultFormData = {
  slh: {
    name: '',
    region: '',
    rank: '',
    gender: '',
    doer: false,
    parent: '',
    bed_count: null,
    address: '',
    website: '',
    weekly_rate: null,
    type: 'no_relationship',
  },
};

export const EditPartner = ({ domain }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateUserDetails } = useUser();
  const [isBtnDisabled, setIsBtnDisabled] = useState(true);
  const [totalBeds, setTotalBeds] = useState(null);
  const [formData, setFormData] = useState(defaultFormData);
  const [form] = Form.useForm();
  const partnerID = getIdFromPath(location.pathname);

  useEffect(() => {
    (async () => {
      const { data } = await getRequestData(endpoints.getPartner(partnerID), domain);

      if (data) {
        const {
          name,
          region,
          rank,
          gender,
          doer,
          parent,
          bed_count,
          address,
          website,
          weekly_rate,
          type,
          fiscal_start_dow,
        } = data;
        const updatedFormData = {
          slh: {
            ...defaultFormData.slh,
            name,
            region,
            parent,
            bed_count,
            address,
            website,
            weekly_rate,
            fiscal_start_dow,
            doer: doer ?? false,
            gender: gender.split(','),
            rank: domain === ROLE_TYPES.admin ? String(rank) : undefined,
            type,
          },
        };
        setTotalBeds(bed_count || 1);
        setFormData(updatedFormData);
        form.setFieldsValue(updatedFormData);
      }
    })();
  }, [domain, form, location.pathname, partnerID]);

  useEffect(() => {
    const isFormValid =
      !!formData.slh.name &&
      !!formData.slh.bed_count &&
      !!formData.slh.weekly_rate &&
      !!formData.slh.gender.length > 0 &&
      domain === ROLE_TYPES.admin
        ? !!formData.slh.rank
        : true;
    setIsBtnDisabled(!isFormValid);
  }, [domain, formData]);

  const handleGenderChange = (genders) => {
    const selectedGenders = genders.join(',');

    setFormData({
      ...formData,
      slh: {
        ...formData.slh,
        gender: selectedGenders,
      },
    });
  };

  const handleRankChange = (e) => {
    const selectedRank = e.target.value;

    setFormData({
      ...formData,
      slh: {
        ...formData.slh,
        rank: selectedRank,
      },
    });
  };

  const onValuesChange = (changedFields, allValues) => {
    allValues.slh.bed_count = totalBeds;

    setFormData({
      ...formData,
      slh: {
        ...formData.slh,
        ...allValues.slh,
      },
    });
  };
  const handleFormSubmit = (values) => {
    values.slh.gender = values.slh.gender.join(',');
    values.slh.bed_count = totalBeds;

    updatePartner(values, partnerID, domain, () => {
      updateUserDetails();
      setTimeout(() => {
        navigate(`/partner/view/${partnerID}`, { replace: true });
      }, 150);
    });
  };

  return (
    <Form
      form={form}
      name="create-client-by-admin"
      initialValues={formData}
      className="flex flex-col w-full gap-6"
      onValuesChange={onValuesChange}
      onFinish={handleFormSubmit}
      requiredMark={false}
    >
      <div className="flex justify-between w-full">
        <div className="flex items-center gap-x-4">
          <div
            onClick={() => navigate(-1)}
            className="w-10 h-10 bg-white rounded-lg shadow-block-shadow flex items-center justify-center cursor-pointer"
          >
            <IconSmallArrow className="w-2 h-4" />
          </div>
          <p className="nunito-title">Edit Partner (SLH)</p>
        </div>
        <OrangeButton type="submit" label="Save" disabled={isBtnDisabled} />
      </div>
      <div className="flex flex-wrap items-start gap-6">
        <div className="flex flex-col w-full base-block pt-[30px]">
          <p className="nunito-title px-[30px] pb-[11px]">SLH</p>
          <div
            className={classNames(
              'flex flex-wrap relative border-t border-color-lightBlue p-[30px] pt-3 gap-x-8 gap-y-6',
              'before:content-[""] before:absolute before:w-4 before:h-4 before:rounded-full before:border before:border-color-divider before:bg-white before:z-10 before:top-0 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2',
            )}
          >
            <FormInput
              label="Name*"
              name={['slh', 'name']}
              placeholder="First Name"
              required
              errorMessage="Please enter valid name"
              className="w-[calc(50%-16px)]"
              value={formData.slh.name}
            />
            <FormInput
              label="Region"
              name={['slh', 'region']}
              placeholder="Enter region"
              className="w-[calc(50%-16px)]"
              value={formData.slh.region}
            />
            <div className="flex w-[calc(50%-16px)]">
              {domain === ROLE_TYPES.admin && (
                <div className="flex flex-col gap-y-2">
                  <div className="flex justify-between items-center">
                    <p className="text-14 font-medium">Classification (Ranking)*</p>
                    <TooltipIcon tooltipText="Tooltip about rank" />
                  </div>
                  <Form.Item name={['slh', 'rank']}>
                    <Radio.Group
                      onChange={handleRankChange}
                      className="w-full flex gap-x-4"
                      value={'1'}
                    >
                      <Radio
                        className="px-4 py-2.5 m-0 bg-white border border-color-orange rounded-xl flex-1"
                        value={'1'}
                      >
                        1
                      </Radio>
                      <Radio
                        className="px-4 py-2.5 m-0 bg-white border !border-color-orange rounded-xl flex-1"
                        value={'2'}
                      >
                        2
                      </Radio>
                      <Radio
                        className="px-4 py-2.5 m-0 bg-white border !border-color-orange rounded-xl flex-1"
                        value={'3'}
                      >
                        3
                      </Radio>
                    </Radio.Group>
                  </Form.Item>
                </div>
              )}
              <div className={`${domain === ROLE_TYPES.admin ? 'ml-auto' : 'flex-1'}`}>
                <div className="flex flex-col gap-y-2">
                  <p className="text-14 font-medium">Beds Count*</p>
                  <div className="flex items-center gap-x-1">
                    <button
                      type="button"
                      onClick={() => setTotalBeds(+totalBeds - 1)}
                      disabled={totalBeds <= 1}
                      className={`w-10 h-10 shadow-block-shadow text-center rounded-ten ${totalBeds <= 1 ? 'opacity-50' : ''}`}
                    >
                      -
                    </button>
                    <input
                      onChange={(e) => {
                        setTotalBeds(e.target.value);
                        setFormData({
                          ...formData,
                          slh: {
                            ...formData.slh,
                            bed_count: e.target.value ? +e.target.value : 1,
                          },
                        });
                      }}
                      type="number"
                      className="w-10 h-10 border border-divider outline-divider outline-1 text-center rounded-ten text-14"
                      value={totalBeds}
                      readOnly
                    />
                    <button
                      type="button"
                      onClick={() => setTotalBeds(+totalBeds + 1)}
                      className="w-10 h-10 shadow-block-shadow text-center rounded-ten"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <FormInput
              label="Address"
              name={['slh', 'address']}
              placeholder="Enter address"
              className="w-[calc(50%-16px)]"
            />
            <div className="flex flex-col gap-y-2 w-[calc(50%-16px)]">
              <p className="text-14 font-medium">Gender*</p>
              <Form.Item name={['slh', 'gender']} required>
                <Checkbox.Group onChange={handleGenderChange} className="w-full gap-x-4">
                  <Checkbox
                    className="px-4 py-2.5 bg-white border font-medium rounded-xl gap-x-2 flex-1"
                    value={'M'}
                  >
                    Male
                  </Checkbox>
                  <Checkbox
                    className="px-4 py-2.5 bg-white border font-medium rounded-xl gap-x-2 flex-1"
                    value={'F'}
                  >
                    Female
                  </Checkbox>
                  <Checkbox
                    className="px-4 py-2.5 bg-white border font-medium rounded-xl gap-x-2 flex-1"
                    value={'O'}
                  >
                    Other
                  </Checkbox>
                </Checkbox.Group>
              </Form.Item>
            </div>
            <div className="flex flex-col gap-y-2 w-[calc(50%-16px)]">
              <div className="flex flex-row justify-between gap-x-6">
                <FormInput
                  label="Website"
                  name={['slh', 'website']}
                  placeholder="Enter website"
                  className="w-[calc(50%-12px)]"
                />

                <FormInput
                  label="Weekly Rate*"
                  type="number"
                  name={['slh', 'weekly_rate']}
                  placeholder="$"
                  required
                  className="w-[calc(50%-12px)]"
                  onKeyDown={allowOnlyDigits}
                  onWheel={(e) => {
                    e.target.blur();
                    e.stopPropagation();

                    setTimeout(() => {
                      e.target.focus();
                    }, 0);
                  }}
                />
              </div>
            </div>
            <div className="flex flex-row justify-between gap-x-2 w-full">
              <div className="flex flex-col gap-y-2  w-[calc(50%-16px)]">
                <p className="text-14 font-medium">D.O.E.R. Program*</p>
                <div className="flex items-center gap-x-3">
                  <IconDoer className="w-8 h-8" />
                  <Form.Item name={['slh', 'doer']} valuePropName="checked">
                    <Checkbox className="px-4 py-2.5 bg-white border font-medium rounded-xl gap-x-2">
                      D.O.E.R. Support
                    </Checkbox>
                  </Form.Item>
                </div>
              </div>
              <div className="flex flex-col gap-y-2  w-[calc(50%-16px)]  ">
                <div className="flex justify-flex-start items-center">
                  <p className="text-14 font-medium">Fiscal Week Start Day</p>
                </div>
                <FormInput
                  // name={['slh', 'type']}
                  value={formData.slh.fiscal_start_dow}
                  disabled
                />
              </div>
            </div>
            <div className="flex gap-x-6 gap-y-2 w-[calc(50%-16px)]">
              <div className="flex flex-col flex-1 gap-y-2">
                <div className="flex justify-flex-start items-center">
                  <p className="text-14 font-medium">Type</p>
                </div>
                <FormInput
                  className="w-full"
                  inputClassName="capitalize"
                  // name={['slh', 'type']}
                  value={
                    formData.slh.type === SLH_TYPES.no_relationship
                      ? 'No Relationship'
                      : formData.slh.type === SLH_TYPES.parent
                        ? 'Parent'
                        : 'Child'
                  }
                  disabled
                />
              </div>
            </div>
            {formData.slh.type === SLH_TYPES.child && (
              <div className="flex flex-col flex-1 gap-y-2">
                <div className="flex justify-flex-start items-center">
                  <p className="text-14 font-medium">Parent</p>
                </div>
                <FormInput
                  inputClassName="capitalize"
                  // name={['slh', 'type']}
                  value={formData.slh.parent?.name}
                  disabled
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </Form>
  );
};
